/**
 * Chrome's Web Animations implementation throws
 * `Cannot read properties of undefined (reading 'startTime')` when an Animation is
 * cancelled while Angular/Material change detection is in flight (menus, dialogs,
 * sidenav, datepickers). Both portals use NoopAnimations and have no other
 * `element.animate` callers, so native WAAPI players are replaced with a finished stub.
 */
const PATCHED_FLAG = '__marinaStartTimePatched';

type AnimateFn = typeof Element.prototype.animate & { [PATCHED_FLAG]?: boolean };

export function patchWebAnimationsStartTime(): void {
  if (typeof Element === 'undefined') {
    return;
  }

  const existing = Element.prototype.animate as AnimateFn | undefined;
  if (existing?.[PATCHED_FLAG]) {
    return;
  }

  const patched: AnimateFn = function marinaSafeAnimate(): Animation {
    return createFinishedAnimationStub();
  };
  patched[PATCHED_FLAG] = true;
  Element.prototype.animate = patched;
}

function createFinishedAnimationStub(): Animation {
  const stub = {
    startTime: 0,
    currentTime: 0,
    timeline: null,
    playbackRate: 1,
    playState: 'finished' as AnimationPlayState,
    pending: false,
    id: '',
    replaceState: 'persisted' as AnimationReplaceState,
    effect: null,
    onfinish: null,
    oncancel: null,
    onremove: null,
    play() {},
    pause() {},
    finish() {},
    cancel() {},
    reverse() {},
    persist() {},
    updatePlaybackRate() {},
    commitStyles() {},
    addEventListener() {},
    removeEventListener() {},
    dispatchEvent() {
      return true;
    },
    finished: Promise.resolve({} as Animation),
    ready: Promise.resolve({} as Animation),
  };
  stub.finished = Promise.resolve(stub as Animation);
  stub.ready = Promise.resolve(stub as Animation);
  return stub as Animation;
}
